import './inspect.css';
import { useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy, type InspectCopyKey } from '@/i18n/ns/inspect';
import type { InspectSession, InspectTargetView } from './types';

const STATUS_COPY: Partial<Record<InspectTargetView['status'], InspectCopyKey>> = {
  stale: 'stale',
  revoked: 'revoked',
  expired: 'expired',
};

function Details({ target }: { target: InspectTargetView }) {
  const copy = useNamespaceCopy(inspectCopy);
  // The source location is there for those who want it, never by default (INSPECTOR-SPEC §2).
  const [showSource, setShowSource] = useState(false);
  const source =
    target.source.status === 'ready'
      ? `${target.source.value.file}:${target.source.value.line}`
      : target.source.status === 'redacted'
        ? copy.sourceHidden
        : target.source.status === 'pending'
          ? copy.pending
          : copy.sourceUnmapped;
  const data =
    target.binding.status === 'ready'
      ? `${target.binding.value.label} · ${target.binding.value.freshness}`
      : target.binding.status === 'pending'
        ? copy.pending
        : copy.dataUnmapped;
  const picture =
    target.crop.status === 'ready'
      ? target.crop.value.captureKind === 'host-render-replay'
        ? copy.pictureReplay
        : null
      : target.crop.status === 'pending'
        ? copy.pending
        : copy.cropUnavailable;
  const row = (term: string, value: string | null | undefined) =>
    value ? (
      <>
        <dt>{term}</dt>
        <dd>{value}</dd>
      </>
    ) : null;
  return (
    <div className="pc-inspect-details" data-testid="inspect-details">
      <dl>
        {row(copy.where, target.where)}
        {row(copy.shows, target.shows)}
        {row(copy.data, data)}
        {row(copy.changesHere, target.changesHere ?? target.scopeLabel)}
        {row(copy.picture, picture)}
        {showSource && row(copy.source, source)}
      </dl>
      {target.source.status !== 'redacted' && (
        <button
          type="button"
          className="pc-link pc-inspect-source-toggle"
          aria-expanded={showSource}
          onClick={() => setShowSource((v) => !v)}
        >
          {showSource ? copy.hideSource : copy.showSource}
        </button>
      )}
    </div>
  );
}

function Chip({
  target,
  numbered,
  onRemove,
}: {
  target: InspectTargetView;
  numbered: boolean;
  onRemove: () => void;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const [open, setOpen] = useState(false);
  const status = STATUS_COPY[target.status];
  return (
    <li className="pc-inspect-chip" data-status={target.status} data-target-id={target.targetId}>
      <span className="pc-inspect-chip-main">
        {numbered && <span className="pc-inspect-order">{target.order}</span>}
        {target.crop.status === 'ready' && (
          <img
            className="pc-inspect-crop"
            src={target.crop.value.url}
            alt=""
            width={20}
            height={20}
          />
        )}
        <button
          type="button"
          className="pc-inspect-chip-label"
          aria-expanded={open}
          title={open ? copy.hideDetails : copy.details}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{target.label}</span>
          {target.context && <span className="pc-inspect-note"> · {target.context}</span>}
          {status && <span className="pc-inspect-chip-status"> · {copy[status]}</span>}
        </button>
        <button
          type="button"
          className="pc-inspect-chip-remove"
          aria-label={formatCopy(copy.remove, { label: target.label })}
          onClick={onRemove}
        >
          ×
        </button>
      </span>
      {open && <Details target={target} />}
    </li>
  );
}

/**
 * The mention chips in the composer, in selection order (`composerChipSlot`). Each chip is a
 * structured object: its label from INS, its slide or page, a small crop when there is one,
 * and details on demand (source, data, scope, picture). Stale or revoked chips say so and are
 * kept until removed; nothing is silently retargeted. Past the limit, the tray explains before
 * anything is dropped.
 */
export function MentionTray({
  session,
  phone = false,
}: {
  session: InspectSession;
  phone?: boolean;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const targets = session.targets;
  if (!targets.length) return null;
  const full = targets.length >= session.limit;
  return (
    <div className="pc-inspect-tray" data-testid="inspect-tray" data-phone={phone || undefined}>
      <ul
        className="pc-inspect-chips"
        aria-label={formatCopy(copy.selectedCount, { n: String(targets.length) })}
      >
        {targets.map((t) => (
          <Chip
            key={t.targetId}
            target={t}
            numbered={targets.length > 1}
            onRemove={() => session.remove(t.targetId)}
          />
        ))}
      </ul>
      {full && (
        <p className="pc-inspect-note" role="status" data-testid="inspect-limit">
          {formatCopy(copy.limit, { limit: String(session.limit) })}
        </p>
      )}
    </div>
  );
}
